import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { BLOCKS, TopLevelBlock } from '@contentful/rich-text-types';
import { Box, Button, Collapse, Link, Typography } from '@mui/material';
import { documentToReactComponents, Options, RenderNode } from '@contentful/rich-text-react-renderer';
import parse from 'html-react-parser';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { ContentTypeRichTextContent } from '@/graphql/__generated/sdk';
import { RichTextContent } from '@/components/RichText/RichText';
import { parseChildren } from '@/components/RichText/RichText.helpers';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import { getCarrotIconStyles } from '@/components/HeaderWithLocationNav/LocationNavBar/LocationDetails/LocationDetails.styles';
import { findFixedHeaderOffset } from '@/utils/windowScrollToElement/windowScrollToElement';

import {
	blogDetailsPageSideNavColumnStyles,
	blogDetailsPageSideNavItemStyles,
	blogDetailsPageSideNavLinkStyles,
	blogDetailsPageSideNavStyles,
	blogDetailsPageSideNavMobileMenuVisibleStyles,
} from './BlogDetailsPageFull.styles';
import { IBlogDetailsPageNavClickProps } from './BlogDetailsPageFull';
import { getScrollOffset, updateNavPosition } from './BlogDetailsPage.helpers';

interface IBlogDatailsPageFullSideNavProps {
	text: ContentTypeRichTextContent;
	handleNavClick: ({ e, which }: IBlogDetailsPageNavClickProps) => void;
	isSmallScreen?: boolean;
}

const getHeaderTwoLinkOptions = ({
	handleNavClick,
}: {
	handleNavClick: ({ e, which }: IBlogDetailsPageNavClickProps) => void;
}): Options => ({
	renderNode: {
		[BLOCKS.HEADING_2]: (_: TopLevelBlock, children) => {
			const which = _.data.id as number;
			const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
				// Reference identifier for navigation
				handleNavClick({ e, which });
			};
			return (
				<Box data-testid={`sidenav-link-${which}`} sx={blogDetailsPageSideNavItemStyles}>
					<Link href={'#'} underline="none" style={blogDetailsPageSideNavLinkStyles} onClick={onLinkClick}>
						<Typography component="span" variant="bodySmallSemiBold">
							{parseChildren(children as React.ReactNode[])}
						</Typography>
						<TAGSvgIcon icon="ChevronRightFunc" size={16} sx={{ marginTop: '0.25rem' }} />
					</Link>
				</Box>
			);
		},
	} as RenderNode,
	renderText: (text) => {
		return text.split('\n').reduce((children, textSegment) => {
			const parsedText = parse(textSegment) as React.ReactNode;
			return [parsedText];
		}, [] as React.ReactNode[]);
	},
});

export default function BlogDetailsPageFullSideNav({
	text,
	handleNavClick,
	isSmallScreen = false,
}: Readonly<IBlogDatailsPageFullSideNavProps>) {
	const [isNavTopThreshold, setIsNavTopThreshold] = useState(false);
	const [isNavBottomLimit, setIsNavBottomLimit] = useState(false);
	const [topOffset, setTopOffset] = useState(0);
	const [targetHeight, setTargetHeight] = useState(0);

	const containerRef = useRef<HTMLDivElement>(null);
	const targetRef = useRef<HTMLDivElement>(null);

	const navFixedOffsetByRem = isSmallScreen ? 0.5 : 1.5;
	const navFixedOffsetByPx = navFixedOffsetByRem * 16;
	const [isMobileMenuVisible, setIsMobileMenuVisible] = useState<boolean>(false);

	const handleScroll = useCallback(() => {
		updateNavPosition({
			containerRef,
			targetRef,
			topOffset,
			navFixedOffsetByRem,
			setTargetHeight,
			setIsNavTopThreshold,
			setIsNavBottomLimit,
		});
	}, [navFixedOffsetByRem, topOffset]);

	useEffect(() => {
		if (!text) return;
		setTopOffset(findFixedHeaderOffset());
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll, text]);

	if (!text?.json) return <></>;

	const content = text.json as RichTextContent;
	const headings = content.content.reduce((acc, curr) => {
		if (curr.nodeType === 'heading-2') {
			// Generate identifier for navigation from rich text
			curr.data = {
				...curr.data,
				id: acc.length,
			};
			acc.push(curr);
		}
		return acc;
	}, [] as RichTextContent['content']);
	if (headings.length === 0) return <></>;

	const filteredContent = {
		...content,
		content: headings,
	};

	const menuTitle = `Table of Contents`;

	const onNavClicked = ({ e, which }: IBlogDetailsPageNavClickProps) => {
		const computedOffset = getScrollOffset({
			navFixedOffsetByPx,
			topOffset,
			targetHeight,
			isSmallScreen,
		});
		handleNavClick({ e, which, offset: computedOffset });
		if (isSmallScreen) setIsMobileMenuVisible(false);
	};

	const handleClick = () => {
		setIsMobileMenuVisible(!isMobileMenuVisible);
	};
	const primaryRichContent = documentToReactComponents(
		filteredContent,
		getHeaderTwoLinkOptions({ handleNavClick: onNavClicked })
	) as ReactNode[];

	return (
		<Box
			ref={containerRef}
			data-testid="blog_detail_page_side_nav_wrapper"
			sx={blogDetailsPageSideNavColumnStyles(isNavTopThreshold, isNavBottomLimit, navFixedOffsetByRem)}
		>
			<Box
				ref={targetRef}
				data-testid="blog_detail_page_side_nav"
				sx={blogDetailsPageSideNavStyles({
					isNavTopThreshold,
					isNavBottomLimit,
					offset: navFixedOffsetByRem,
					parentContainerTop: topOffset,
					isSmallScreen: isSmallScreen,
				})}
				component={'nav'}
			>
				{isSmallScreen ? (
					<>
						<Button
							id="navigation-button"
							data-testid="sidenav-mobile-menu-button"
							fullWidth={true}
							aria-expanded={isMobileMenuVisible ? 'true' : undefined}
							sx={{ justifyContent: 'space-between' }}
							onClick={handleClick}
						>
							<Typography variant="header4" paddingX={{ sm: 0, md: '0.5rem' }}>
								{menuTitle}
							</Typography>
							<KeyboardArrowDownIcon sx={getCarrotIconStyles(isMobileMenuVisible)} />
						</Button>
						<Collapse in={isMobileMenuVisible} sx={blogDetailsPageSideNavMobileMenuVisibleStyles}>
							{primaryRichContent}
						</Collapse>
					</>
				) : (
					<>
						<Typography variant="header4" paddingX="0.5rem" marginBottom={'0.5rem'}>
							{menuTitle}
						</Typography>
						{primaryRichContent}
					</>
				)}
			</Box>
		</Box>
	);
}
